import { CommonModule } from '@angular/common';
import {
    Component,
    DoCheck,
    ElementRef,
    EventEmitter,
    HostBinding,
    HostListener,
    Inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    Renderer2,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import { App } from '@df/core/app';
import { getElementHeight } from '@df/dom/fn/get-element-height';
import { WINDOW } from '@df/dom/globals';
import { OnChange } from '@df/ng/on-change';
import { DraggingService } from '@df/ui/dragging-service';
import { NoScrollService } from '@df/ui/no-scroll.service';
import { TrayHandleComponent } from './tray-handle.component';
import { TrayService } from './tray.service';
import { injectTimeoutService } from '@df/ng/timeout.service';

@Component({
    selector: 'tray',
    templateUrl: './tray.component.html',
    styleUrl: './tray.component.scss',
    standalone: true,
    imports: [CommonModule, TrayHandleComponent]
})
export class TrayComponent implements OnInit, OnDestroy, OnChanges, DoCheck {
    ///////////// INPUTS ///////////////

    /**
     * If cannot be resized
     */
    @Input()
    @HostBinding('class.is-static')
    static = false;

    /**
     * If cannot be resized
     */
    @Input()
    priority?: number;

    /**
     * Sequence name (if you want to separate trays stack)
     */
    @Input()
    sequence = 'default';

    /**
     * Leave empty if you want tray to be closed by default
     * Set value to make open by default
     * Number will be max percent of viewport height saved for the tray that can be used
     * For example
     * open="50" will open the tray but max 50% of viewport size can be used, if tray is longer then it will
     * be partially closed
     */
    @Input()
    open?: string | number;

    /**
     * Fully open tray size min-shift from top in vw
     */
    @Input()
    fullOpen?: number;

    /**
     * Resize trigger - change it to trigger resize
     */
    @Input()
    resizeTrigger: any;

    @Input()
    narrow = false;

    @Output()
    openTray = new EventEmitter<boolean>();

    @Output()
    closeTray = new EventEmitter<boolean>();

    /**
     * Handle element
     */
    @ViewChild(TrayHandleComponent)
    trayHandle?: TrayHandleComponent;

    /**
     * Cleanup method
     */
    private release?: () => void;

    /**
     * Last tray height (in case contents change we can keep the position)
     */
    private lastHeight?: number;

    /**
     * Fold element
     */
    // @ContentChild(TrayFoldComponent)
    // trayFold?: TrayFoldComponent;
    get trayFoldHeight() {
        const elem = (<HTMLElement>this.elementRef.nativeElement).getElementsByTagName('tray-fold')[0];

        return elem ? Math.ceil(getElementHeight(elem)) : 0;
    }

    ///////////// DISPLAY ///////////////

    /**
     * Element height
     */
    get height(): number {
        return Math.round(getElementHeight(this.elementRef.nativeElement));
    }

    /**
     * Top shift
     */
    @OnChange(function () {
        const that = this as TrayComponent;

        if (that.static) {
            return;
        }

        that.renderer2.setStyle(that.elementRef.nativeElement, 'top', this.top + 'px');
    })
    top: number;

    @HostBinding('style.z-index')
    zIndex = 5;

    ///////////// HELPERS ///////////////
    /**
     * If is being dragged
     */
    private isDrag = false;

    /**
     * Last y position of mouse event (for dragging)
     */
    private yStart?: number;

    private initiated = false;
    private hidden = false;

    private animationTimeout?: number;

    private closingTimeout?: number;

    protected timeoutService = injectTimeoutService();

    /**
     * Dependencies
     */
    constructor(
        private elementRef: ElementRef,
        private renderer2: Renderer2,
        private trayService: TrayService,
        private noScrollService: NoScrollService,
        private draggingService: DraggingService,
        public app: App,
        @Inject(WINDOW) private window: Window
    ) {}

    ngOnInit(): void {
        if (this.app.isPlatformBrowser) {
            this.trayService.register(this);

            setTimeout(() => {
                this.initiateTrayHeight();
                this.initiated = true;
            }, 200); // give contents a chance to render
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (this.initiated && changes['resizeTrigger']) {
            this.initiateTrayHeight();
        }
    }

    /**
     * Update position on every check
     */
    ngDoCheck() {
        this.trayService.updateAll();
    }

    ngOnDestroy() {
        this.closeTray.emit(true);

        if (this.release) {
            this.release();
        }

        this.trayService.unregister(this);
    }

    private initiateTrayHeight() {
        this.timeoutService.setTimeout(() => {
            if (this.open) {
                this.updatePosition(
                    -Math.round(
                        this.procToPx(100 - Number(this.open)) -
                            this.trayService.getPriorityShift(this) -
                            (this.top === undefined ? this.window.innerHeight : this.top)
                    )
                );

                this.openTray.emit(true);
            } else {
                this.close();
            }
            // we only show the frame after it is positioned not to have any jump
            this.renderer2.setStyle(this.elementRef.nativeElement, 'opacity', 1);
        }, 200);
    }

    private procToPx(winPercVal: number) {
        return (winPercVal / 100) * this.window.innerHeight;
    }

    ///////////// DRAGGING ///////////////

    @HostListener('mousedown', ['$event'])
    onMouseDown(event: MouseEvent) {
        if (!this.static) {
            this.setDrag(true);
            this.yStart = event.clientY;
        }
    }

    @HostListener('touchstart', ['$event'])
    onTouchStart(event: TouchEvent) {
        if (!this.static) {
            this.setDrag(true);
            this.yStart = event.touches[0].clientY;
        }
    }

    /**
     * required for mobile browser nav hiding after scrolling page content, it requires tray position update;
     */
    @HostListener('document:mousedown')
    @HostListener('document:touchstart')
    onDocumentMouseDown() {
        if (!this.static) {
            this.closeDebounced();
        }
    }

    @HostListener('document:mouseup')
    @HostListener('document:touchend')
    onMouseUp() {
        this.setDrag(false);
    }

    @HostListener('document:mousemove', ['$event'])
    onMouseMove(event: MouseEvent) {
        if (this.isDrag && !this.draggingService.isLocked) {
            this.shiftTo(event.clientY);
        }
    }

    @HostListener('document:touchmove', ['$event'])
    onTouchMove(event: TouchEvent) {
        if (this.isDrag && !this.draggingService.isLocked) {
            this.shiftTo(event.touches[0].clientY);
        }
    }

    private closeDebounced() {
        this.cancelCloseDebounced();
        this.closingTimeout = this.timeoutService.setTimeout(() => {
            if (!this.isDrag && !this.draggingService.isLocked) {
                this.fullyClose();
            }
        }, 200);
    }

    private cancelCloseDebounced() {
        clearTimeout(this.closingTimeout);
    }

    private setDrag(to: boolean) {
        if (to !== this.isDrag) {
            if (to) {
                this.clearAnimation();
                this.cancelCloseDebounced();
                this.timeoutService.setTimeout(() => this.cancelCloseDebounced(), 50);
            }
            this.isDrag = to;
            if (to) {
                this.release = this.noScrollService.block();
            } else {
                if (this.release) {
                    this.release();
                }
            }
        }
    }

    /**
     * Shift tray by as many pixesls as mouse moved
     */
    private shiftTo(y: number) {
        this.trayService.updatePosition(this, this.yStart - y);
        this.yStart = y;
    }

    ///////////// DISPLAY ///////////////

    /**
     * Make smallest allowed
     */
    private close() {
        // we need to call service here as it may affect other trays
        this.trayService.updatePosition(this, -Infinity);
    }

    fullyOpen(force = false) {
        if (!this.isDrag || force) {
            this.hidden = false;
            this.setAnimation();
            this.updatePosition(Infinity, { minTop: this.fullOpen ? Math.round((this.fullOpen * this.window.innerWidth) / 100) : 0 });
        }
    }

    fullyClose(force = false) {
        if (!this.isDrag || force) {
            this.hidden = true;
            this.setAnimation();
            this.updatePosition(-Infinity);
        }
    }

    private setAnimation() {
        clearTimeout(this.animationTimeout);
        this.renderer2.addClass(this.elementRef.nativeElement, 'is-animate');
        this.animationTimeout = this.timeoutService.setTimeout(() => this.clearAnimation(), 1000);
    }

    private clearAnimation() {
        this.renderer2.removeClass(this.elementRef.nativeElement, 'is-animate');
    }

    updatePosition(
        shift: number = 0,
        options?: Partial<{
            minTop: number;
        }>
    ) {
        this.cancelCloseDebounced();
        const priorityShift = this.trayService.getPriorityShift(this);
        let bottom: number;
        const height = this.height;

        if (this.static && !this.hidden) {
            bottom = priorityShift;
        } else {
            // this.lastHeight = height;
            const min = priorityShift + -height + (this.trayHandle?.height || 0) + this.trayFoldHeight;
            const max = priorityShift;
            const value = -((this.top === undefined ? window.innerHeight : this.top) - this.window.innerHeight + height) + shift;

            bottom = Math.round(Math.min(Math.max(value, min), max));
        }
        let top = this.window.innerHeight - height - bottom;
        if (options?.minTop) {
            top = Math.max(top, options?.minTop);
        }

        if (top !== this.top) {
            this.top = top;
        }
        this.zIndex = this.trayService.getZIndex(this);
    }

    /**
     * Gets how much of the window height this element takes
     */
    get heightUse(): number {
        return Math.max(0, this.window.innerHeight - this.top);
    }
}
